/**
 * Courses near a point — the phone, or the course of the round before.
 *
 * Distance sits BESIDE the society's own order (utils/courseOrder.js), never
 * blended into it. "Most played" answers where do we usually go; "nearby"
 * answers where am I. One score made of both cannot say why a row is third,
 * so the pickers show sections that each explain themselves:
 *
 *   NEAR YOU            up to NEARBY_SHOWN within NEARBY_RADIUS_KM, nearest first
 *   YOUR SOCIETY PLAYS  the top FAVOURITES_SHOWN by courseOrder
 *   ALL COURSES         everything else, in courseOrder
 *
 * A course appears ONCE, in the highest section it qualifies for, carrying
 * both labels; the favourites backfill so they stay three long. With no anchor
 * this is exactly the list that shipped before, under two headings.
 *
 * Everything here is pure, and the position it is handed never leaves the
 * device: the society's list already carries coordinates (`/courses`), so the
 * distance is worked out here rather than by asking the server.
 * docs/nearby-courses-design.md has the reasoning.
 */
import { orderCourses, isPlayed, playedLabel, FAVOURITES_SHOWN } from './courseOrder';

export const KM_PER_MILE = 1.609344;
export const NEARBY_RADIUS_MILES = 25;
export const NEARBY_RADIUS_KM = NEARBY_RADIUS_MILES * KM_PER_MILE;
export const NEARBY_SHOWN = 3;

/**
 * "You're here": the nearest course is this close AND the round is today.
 * Not a few hundred metres, because a course's pin is its POSTCODE's
 * centroid, and a rural UK postcode can sit a kilometre from the clubhouse.
 */
export const HERE_RADIUS_KM = 1.5;
/** A fix vaguer than this cannot tell the car park from the next village. */
export const HERE_MAX_ACCURACY_M = 1000;

const EARTH_RADIUS_KM = 6371.0088;
const rad = (deg) => (deg * Math.PI) / 180;

/** Great-circle distance between two {lat, lng} points, in km. */
export function distanceKm(a, b) {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** A course's pin as {lat, lng}, or null where it has none. */
export function courseLocation(course) {
  const lat = course?.latitude;
  const lng = course?.longitude;
  if (lat == null || lng == null || lat === '' || lng === '') return null;
  const point = { lat: Number(lat), lng: Number(lng) };
  return Number.isFinite(point.lat) && Number.isFinite(point.lng) ? point : null;
}

/** How many of these courses can never be "near" anything. */
export const countUnlocated = (courses) =>
  (courses || []).filter((c) => !courseLocation(c)).length;

/**
 * Miles where golfers think in miles. The app is UK-first, so a bare
 * language with no region ("en") reads as miles too.
 */
export function usesMiles(locale) {
  const tag = String(locale || '');
  const region = (tag.split(/[-_]/)[1] || '').toUpperCase();
  if (!region) return tag === '' || tag.toLowerCase().startsWith('en');
  return ['GB', 'US', 'LR', 'MM'].includes(region);
}

/** "2.1 mi", "14 mi", "3.4 km". */
export function formatDistance(km, miles = true) {
  const value = miles ? km / KM_PER_MILE : km;
  const unit = miles ? 'mi' : 'km';
  if (value < 10) return `${Math.max(value, 0.1).toFixed(1)} ${unit}`;
  return `${Math.round(value)} ${unit}`;
}

/** The radius as people read it: "25 miles" / "40 km". */
export const formatRadius = (miles = true) =>
  (miles ? `${NEARBY_RADIUS_MILES} miles` : `${Math.round(NEARBY_RADIUS_KM)} km`);

/**
 * The line beside a course — why it is where it is.
 *
 * `entry` is one row from `sectionCourses`. Only ever says what it knows: no
 * distance for a course with no pin, no "0 times" for one never played.
 */
export function courseMeta(entry, { miles = true } = {}) {
  const parts = [];
  if (entry.here) parts.push("You're here");
  else if (entry.isAnchor) parts.push(entry.anchorLabel || 'Same course');
  else if (entry.km != null) parts.push(formatDistance(entry.km, miles));
  const played = playedLabel(entry.course);
  if (played) parts.push(played);
  return parts.join(' · ');
}

/**
 * The picker's list, in sections.
 *
 * - `anchor`: {lat, lng, accuracy?, courseId?, label?} or null. A COURSE
 *   anchor (multi-round, Round 2+) carries `courseId`, and that course's own
 *   row says `label` ("Round 1 course") rather than "0.1 mi".
 * - `today`: the round is being played today — the only time "You're here"
 *   can be true. A course anchor is never "here".
 * - `search`: typing collapses the sections into ONE filtered list in the
 *   society's order (what shipped before), and the rows keep their distance:
 *   "Royal…" with two results is answered by 1.8 mi vs 212 mi.
 *
 * Returns `{ searching, rows }` while searching, otherwise
 * `{ searching, nearby, favourites, rest }`; every row is
 * `{ course, km, here, isAnchor, anchorLabel }`.
 */
export function sectionCourses({ courses, anchor = null, today = false, search = '' }) {
  const term = String(search || '').trim().toLowerCase();
  const matching = (courses || []).filter(
    (c) => !term || String(c.name || '').toLowerCase().includes(term));

  const entry = (course) => {
    const at = anchor ? courseLocation(course) : null;
    const isAnchor = anchor?.courseId != null && course.id === anchor.courseId;
    return {
      course,
      km: at ? distanceKm(anchor, at) : null,
      here: false,
      isAnchor,
      anchorLabel: isAnchor ? anchor.label : undefined,
    };
  };

  if (term) {
    return { searching: true, rows: orderCourses(matching).map(entry) };
  }

  const ordered = orderCourses(matching);
  const rank = new Map(ordered.map((c, i) => [c.id, i]));
  const all = ordered.map(entry);

  const nearby = all
    .filter((e) => e.km != null && e.km <= NEARBY_RADIUS_KM)
    // Nearest first; the society's order settles a dead heat.
    .sort((a, b) => (a.km - b.km) || (rank.get(a.course.id) - rank.get(b.course.id)))
    .slice(0, NEARBY_SHOWN);

  const first = nearby[0];
  if (first && today && anchor?.courseId == null
      && first.km <= HERE_RADIUS_KM
      && (anchor.accuracy == null || anchor.accuracy <= HERE_MAX_ACCURACY_M)) {
    first.here = true;
  }

  const shown = new Set(nearby.map((e) => e.course.id));
  const favourites = all
    .filter((e) => !shown.has(e.course.id) && isPlayed(e.course))
    .slice(0, FAVOURITES_SHOWN);
  favourites.forEach((e) => shown.add(e.course.id));
  const rest = all.filter((e) => !shown.has(e.course.id));

  return { searching: false, nearby, favourites, rest };
}

/** Today's date as the wizard stores it (local, YYYY-MM-DD). */
export function localIsoDate(d = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * A position as it may leave the phone: 2 decimal places, about a kilometre.
 * Only the directory lookup sends one (`/global-courses/nearby`), and the
 * society's own list never needs to — see docs/nearby-courses-design.md §2.1.
 */
export const roundedForServer = (point) => ({
  lat: Math.round(point.lat * 100) / 100,
  lng: Math.round(point.lng * 100) / 100,
});
